<!-- src/shared/components/ui/SplitButton.vue -->

// src/components/SplitButton.vue

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { ref, computed, nextTick, onMounted } from 'vue';
  import { useMagicKeys, whenever } from '@vueuse/core';

  const { t } = useI18n();

  // Action types for the button
  type ActionType = 'create-link' | 'generate-password';

  const props = defineProps({
    content: { type: String, default: '' },
    withGenerate: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    disableGenerate: { type: Boolean, default: false },
    cornerClass: { type: String, default: '' },
    primaryColor: { type: String, default: '' },
    buttonTextLight: { type: Boolean, default: undefined },
    /** Whether keyboard shortcuts (Cmd/Ctrl+Enter) should trigger the main action */
    keyboardShortcutEnabled: { type: Boolean, default: false },
    /** Whether to display the keyboard shortcut hint in the button */
    showKeyboardHint: { type: Boolean, default: false },
    /** Optional subtitle shown below button label on small screens */
    subtitle: { type: String, default: '' },
  });

  const emit = defineEmits(['update:action']);

  const submitButtonRef = ref<HTMLButtonElement | null>(null);
  const selectedAction = ref<ActionType>('create-link');
  const isContentEmpty = computed(() => !props.content.trim());
  const isCreateButtonDisabled = computed(() => props.disabled || (!isContentEmpty.value && !props.content));
  const isGenerateButtonDisabled = computed(() => props.disableGenerate);

  const processCornerClass = (cornerClass: string | undefined): { leftCorner: string, rightCorner: string } => {
    if (!cornerClass) {
      return { leftCorner: 'rounded-l-xl', rightCorner: 'rounded-r-xl' };
    }

    // Extract radius size from cornerClass (e.g., 'lg', 'md', 'sm', etc.)
    const match = cornerClass.match(/rounded-(\w+)/);
    const size = match ? match[1] : 'xl';

    return {
      leftCorner: `rounded-l-${size}`,
      rightCorner: `rounded-r-${size}`,
    };
  }

  // Get the correct equivalent left and right corner classes
  const corners = computed(() => processCornerClass(props.cornerClass));
  const createButtonCornerClass = computed(() => props.withGenerate ? 'rounded-lg' : props.cornerClass || 'rounded-lg');
  const textColorClass = computed(() => props.buttonTextLight ? 'text-white' : 'text-gray-800');
  // Left button focus ring (respects left corner rounding)
  const leftButtonFocusClass = computed(() => createButtonCornerClass.value);

  // Right button focus ring (respects right corner rounding)
  const rightButtonFocusClass = computed(() => props.withGenerate ? 'rounded-lg' : `${corners.value.rightCorner}`);

  // Compute the ring color based on primaryColor availability
  const ringColorStyle = computed(() => props.primaryColor ? props.primaryColor : 'var(--color-brand-600)');
  const createButtonStyle = computed(() => props.withGenerate
    ? {
      backgroundColor: '#FFFFFF',
      borderColor: '#0F4089',
      color: '#0F4089',
      '--tw-ring-color': '#0F4089',
      '--tw-ring-opacity': '0.2',
      '--button-shadow-color': '#0F4089',
    }
    : {
      backgroundColor: `${props.primaryColor}`,
      borderColor: `${props.primaryColor}`,
      '--tw-ring-color': ringColorStyle.value,
      '--tw-ring-opacity': '0.2',
      '--button-shadow-color': props.primaryColor || 'var(--color-brand-500)',
    });
  const generateButtonStyle = computed(() => ({
    backgroundColor: '#0F4089',
    borderColor: '#FFFFFF',
    color: '#FFFFFF',
    '--tw-ring-color': '#0F4089',
    '--tw-ring-opacity': '0.2',
    '--button-shadow-color': '#0F4089',
  }));

  const createButtonLabel = computed(() => t('web.LABELS.create_link_short'));
  const generateButtonLabel = computed(() => t('web.COMMON.button_generate_secret_short'));

  function setAction(action: ActionType) {
    selectedAction.value = action;
    emit('update:action', action);
  }

  async function handleGenerateClick() {
    if (isGenerateButtonDisabled.value) return;
    setAction('generate-password');
    await nextTick();
    submitButtonRef.value?.form?.requestSubmit();
  }

  onMounted(() => {
    // Emit initial action
    emit('update:action', selectedAction.value);
  });

  // Platform detection for keyboard hint
  const isMac = computed(() =>
    typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)
  );
  const shortcutHint = computed(() => (isMac.value ? '⌘ Enter' : 'Ctrl Enter'));

  // Keyboard shortcut: Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
  const keys = useMagicKeys();
  const submitShortcut = computed(
    () => props.keyboardShortcutEnabled && (keys['Meta+Enter'].value || keys['Control+Enter'].value)
  );

  whenever(submitShortcut, () => {
    if (!isCreateButtonDisabled.value) {
      // Programmatically click the submit button to trigger native form submission
      submitButtonRef.value?.click();
    }
  });
</script>

<template>
  <div
    class="relative inline-flex w-full gap-2 sm:w-auto"
    role="group">
    <!-- Visually hidden announcement for screen readers when action changes -->
    <div
      v-if="selectedAction"
      class="sr-only"
      aria-live="assertive">
      {{ selectedAction === 'create-link' ? createButtonLabel : generateButtonLabel }} mode activated
    </div>
    <button
      ref="submitButtonRef"
      type="submit"
      data-testid="split-button-submit"
      :class="[
        createButtonCornerClass,
        textColorClass,
        leftButtonFocusClass,
        'flex flex-1 items-center justify-center gap-2.5 px-5 py-3.5 text-lg font-semibold',
        'transition-all relative overflow-hidden group',
        'border',
        'focus:z-10 focus:outline-none focus:ring-4 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
        'hover:shadow-[0_8px_20px_-4px_var(--button-shadow-color)]',
        'active:scale-[0.98]',
        {
          'cursor-not-allowed': isCreateButtonDisabled,
        },
      ]"
      :style="createButtonStyle"
      :disabled="isCreateButtonDisabled"
      :aria-label="createButtonLabel"
      @click="setAction('create-link')">
      <!-- Gradient overlay for depth -->
      <span class="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></span>

      <span class="flex items-center text-current relative z-10 transition-transform group-hover:scale-110">
        <!-- Create Link Icon -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="size-5">
          <rect x="3"
y="11"
width="18"
height="11"
rx="2"
ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </span>
      <span class="relative z-10 flex flex-col items-start leading-tight">
        <span>{{ createButtonLabel }}</span>
        <span
          v-if="subtitle"
          class="text-xs font-normal opacity-75 sm:hidden">
          {{ subtitle }}
        </span>
      </span>
      <kbd
        v-if="showKeyboardHint"
        class="ml-1.5 hidden rounded bg-white/20 px-1.5 py-0.5
          text-xs font-normal opacity-70 sm:inline-block relative z-10">
        {{ shortcutHint }}
      </kbd>
    </button>

    <!-- prettier-ignore-attribute class -->
    <button
      v-if="props.withGenerate"
      type="button"
      :class="[
        corners.rightCorner,
        textColorClass,
        rightButtonFocusClass,
        'flex items-center justify-center gap-2.5 relative overflow-hidden group',
        'border',
        'focus:z-10 focus:outline-none focus:ring-4 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
        'px-5 py-3.5 text-lg font-semibold transition-all',
        'hover:opacity-100 hover:shadow-[0_8px_20px_-4px_var(--button-shadow-color)]',
        'active:scale-[0.98]',
        {
          'cursor-not-allowed': isGenerateButtonDisabled,
        },
      ]"
      :style="generateButtonStyle"
      :disabled="isGenerateButtonDisabled"
      @click="handleGenerateClick"
      data-testid="split-button-generate"
      :aria-label="generateButtonLabel">
      <!-- Gradient overlay for depth -->
      <span class="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true"></span>

      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="size-5 relative z-10 transition-transform group-hover:scale-110">
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </svg>
      <span class="relative z-10 hidden sm:inline">{{ generateButtonLabel }}</span>
    </button>
  </div>
</template>
